import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './OrderDetail.css';
import { formatINR } from '../../utils/currency';

const OrderDetail = () => {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && orderId) {
      fetchOrder();
    }
  }, [user, orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/orders/${orderId}`);
      if (response.data.success) {
        setOrder(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: '#ffc107',
      confirmed: '#17a2b8',
      processing: '#007bff',
      shipped: '#6f42c1',
      delivered: '#28a745',
      cancelled: '#dc3545',
      refunded: '#6c757d',
      returned: '#fd7e14'
    };
    return statusColors[status] || '#6c757d';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
      returned: 'Returned'
    };
    return statusTexts[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="order-detail-container">
        <div className="order-detail-empty">
          <h2>Please log in to view order details</h2>
          <Link to="/login" className="btn btn-primary">Login</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="order-detail-container">
        <div className="order-detail-loading">
          <h2>Loading order details...</h2>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-container">
        <div className="order-detail-error">
          <h2>Error loading order</h2>
          <p>{error || 'Order not found'}</p>
          <Link to="/ecommerce/orders" className="btn btn-primary">Back to Orders</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-detail-container">
      <div className="order-detail-header">
        <Link to="/ecommerce/orders" className="back-link">← Back to Orders</Link>
        <h1>Order #{order.orderNumber}</h1>
        <p>Placed on {formatDate(order.createdAt)}</p>
      </div>

      <div className="order-detail-content">
        <div className="order-status-section">
          <h2>Order Status</h2>
          <div className="status-info">
            <span 
              className="status-badge"
              style={{ backgroundColor: getStatusColor(order.status) }}
            >
              {getStatusText(order.status)}
            </span>
            <p className="status-description">
              {order.status === 'pending' && 'Your order is being processed and will be confirmed shortly.'}
              {order.status === 'confirmed' && 'Your order has been confirmed and is being prepared.'}
              {order.status === 'processing' && 'Your order is being prepared for shipment.'}
              {order.status === 'shipped' && 'Your order has been shipped and is on its way.'}
              {order.status === 'delivered' && 'Your order has been delivered successfully.'}
              {order.status === 'cancelled' && 'Your order has been cancelled.'}
              {order.status === 'refunded' && 'Your order has been refunded.'}
              {order.status === 'returned' && 'Your order has been returned.'}
            </p>
          </div>
        </div>

        <div className="order-items-section">
          <h2>Order Items</h2>
          <div className="order-items">
            {order.items.map((item, index) => (
              <div key={index} className="order-item">
                <div className="item-image">
                  <img 
                    src={item.productImage || '/placeholder-product.jpg'} 
                    alt={item.productName}
                  />
                </div>
                <div className="item-details">
                  <h3>{item.productName}</h3>
                  <p className="item-code">SKU: {item.productCode}</p>
                  {item.variant && (
                    <p className="item-variant">
                      {item.variant.name}: {item.variant.value}
                    </p>
                  )}
                  <p className="item-quantity">Quantity: {item.quantity}</p>
                </div>
                <div className="item-price">
                  <span>{formatINR(item.totalPrice)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="order-summary-section">
          <h2>Order Summary</h2>
          <div className="order-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>{formatINR(order.subtotal)}</span>
            </div>
            {order.taxAmount > 0 && (
              <div className="summary-row">
                <span>Tax:</span>
                <span>{formatINR(order.taxAmount)}</span>
              </div>
            )}
            {order.shippingAmount > 0 && (
              <div className="summary-row">
                <span>Shipping:</span>
                <span>{formatINR(order.shippingAmount)}</span>
              </div>
            )}
            {order.discountAmount > 0 && (
              <div className="summary-row discount">
                <span>Discount:</span>
                <span>-{formatINR(order.discountAmount)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total:</span>
              <span>{formatINR(order.totalAmount)}</span>
            </div>
          </div>
        </div>

        <div className="order-addresses">
          <div className="shipping-address">
            <h3>Shipping Address</h3>
            <div className="address-details">
              <p>{order.shippingAddress.firstName} {order.shippingAddress.lastName}</p>
              {order.shippingAddress.company && <p>{order.shippingAddress.company}</p>}
              <p>{order.shippingAddress.address1}</p>
              {order.shippingAddress.address2 && <p>{order.shippingAddress.address2}</p>}
              <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</p>
              <p>{order.shippingAddress.country}</p>
              {order.shippingAddress.phone && <p>Phone: {order.shippingAddress.phone}</p>}
            </div>
          </div>

          <div className="billing-address">
            <h3>Billing Address</h3>
            <div className="address-details">
              <p>{order.billingAddress.firstName} {order.billingAddress.lastName}</p>
              {order.billingAddress.company && <p>{order.billingAddress.company}</p>}
              <p>{order.billingAddress.address1}</p>
              {order.billingAddress.address2 && <p>{order.billingAddress.address2}</p>}
              <p>{order.billingAddress.city}, {order.billingAddress.state} {order.billingAddress.zipCode}</p>
              <p>{order.billingAddress.country}</p>
              {order.billingAddress.phone && <p>Phone: {order.billingAddress.phone}</p>}
            </div>
          </div>
        </div>

        {order.trackingNumber && (
          <div className="tracking-section">
            <h3>Tracking Information</h3>
            <p><strong>Tracking Number:</strong> {order.trackingNumber}</p>
            {order.carrier && <p><strong>Carrier:</strong> {order.carrier}</p>}
            {order.estimatedDelivery && (
              <p><strong>Estimated Delivery:</strong> {formatDate(order.estimatedDelivery)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;
